/**
 * opora-account-file.js — Opora File per account
 * Single account_id (Clerk user.id) is the primary key for all saved work.
 * Every tool read/write includes account_id via headers + body.
 */
(function (global) {
  'use strict';

  var STORAGE_KEY = 'opora_account_id';
  var ACTIVE_FILE_KEY = 'opora_active_file_id';
  var _accountId = null;
  var _ready = false;

  function readStoredId() {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch (e) { return null; }
  }

  function persistId(id) {
    _accountId = id || null;
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
    if (id) {
      global.dispatchEvent(new CustomEvent('opora:account-ready', { detail: { account_id: id } }));
    }
  }

  function shortId(id) {
    if (!id) return '';
    if (id.length <= 12) return id;
    return id.slice(0, 6) + '…' + id.slice(-4);
  }

  async function resolveFromClerk() {
    try {
      if (global.Clerk && global.Clerk.user && global.Clerk.user.id) {
        return global.Clerk.user.id;
      }
      if (global.Clerk && !global.Clerk.loaded && typeof global.Clerk.load === 'function') {
        await global.Clerk.load();
      }
      if (global.Clerk && global.Clerk.user && global.Clerk.user.id) {
        return global.Clerk.user.id;
      }
    } catch (e) {}
    return readStoredId();
  }

  async function init() {
    var id = await resolveFromClerk();
    if (id) persistId(id);
    _ready = true;
    patchFetchApi();
    if (global.OporaAccount && typeof global.OporaAccount.onAuthChange === 'function') {
      global.OporaAccount.onAuthChange(function (ev) {
        if (ev.signedIn) {
          resolveFromClerk().then(function (uid) { if (uid) persistId(uid); });
        } else {
          persistId(null);
        }
      });
    }
    return id;
  }

  function getId() {
    return _accountId || readStoredId();
  }

  function getActiveFileId() {
    try { return sessionStorage.getItem(ACTIVE_FILE_KEY) || null; } catch (e) { return null; }
  }

  function setActiveFileId(fileId) {
    try {
      if (fileId) sessionStorage.setItem(ACTIVE_FILE_KEY, fileId);
      else sessionStorage.removeItem(ACTIVE_FILE_KEY);
    } catch (e) {}
    global.dispatchEvent(new CustomEvent('opora:file-active', { detail: { file_id: fileId } }));
  }

  function accountHeaders() {
    var id = getId();
    return id ? { 'X-Opora-Account-Id': id } : {};
  }

  function withAccountBody(body) {
    body = body && typeof body === 'object' ? Object.assign({}, body) : {};
    var id = null;
    try {
      if (global.Clerk && global.Clerk.user && global.Clerk.user.id) {
        id = global.Clerk.user.id;
      }
    } catch (e) {}
    if (!id) id = getId();
    if (id) body.account_id = id;
    var active = getActiveFileId();
    if (active) body.parent_file_id = body.parent_file_id || active;
    return body;
  }

  function scopedUrl(path) {
    var id = getId();
    if (!id) return path;
    var sep = path.indexOf('?') >= 0 ? '&' : '?';
    return path + sep + 'account_id=' + encodeURIComponent(id);
  }

  function patchFetchApi() {
    var edge = global.OporaEdge;
    if (!edge || edge.__accountFilePatched || typeof edge.fetchApi !== 'function') return;
    var orig = edge.fetchApi.bind(edge);
    edge.fetchApi = function (path, opts) {
      opts = opts || {};
      var headers = Object.assign({}, opts.headers || {}, accountHeaders());
      if (opts.body && typeof opts.body === 'string' && path.indexOf('/api/') !== -1) {
        try {
          var parsed = JSON.parse(opts.body);
          opts.body = JSON.stringify(withAccountBody(parsed));
        } catch (e) {}
      }
      var scopedPath = path;
      if (path.indexOf('/api/library') !== -1 || path.indexOf('/api/edge/library') !== -1 || path.indexOf('/api/pipeline') !== -1) {
        scopedPath = scopedUrl(path);
      }
      return orig(scopedPath, Object.assign({}, opts, { headers: headers }));
    };
    edge.__accountFilePatched = true;
  }

  async function apiFetch(path, opts) {
    opts = opts || {};
    if (global.OporaEdge && typeof global.OporaEdge.fetchApi === 'function') {
      return global.OporaEdge.fetchApi(scopedUrl(path), opts);
    }
    var base = (global.OPORA_WORKER_URL || 'https://api.oporasupply.com').replace(/\/$/, '');
    var headers = Object.assign({ 'Content-Type': 'application/json' }, accountHeaders(), opts.headers || {});
    if (global.OporaAccount && typeof global.OporaAccount.getAuthHeaders === 'function') {
      headers = Object.assign({}, await global.OporaAccount.getAuthHeaders(), headers);
    }
    var body = opts.body;
    if (body && typeof body === 'string') {
      try { body = JSON.stringify(withAccountBody(JSON.parse(body))); } catch (e) {}
    }
    return fetch(base + scopedUrl(path.charAt(0) === '/' ? path : '/' + path), Object.assign({}, opts, { headers: headers, body: body }));
  }

  async function listFiles() {
    var res = await apiFetch('/api/edge/library/list');
    if (!res.ok) {
      res = await apiFetch('/api/library/list');
    }
    if (!res.ok) throw new Error('Could not load files');
    var data = await res.json();
    var envelope = data.data || data;
    return envelope.items || envelope.files || data.items || data.artifacts || [];
  }

  async function saveFile(opts) {
    opts = opts || {};
    var body = withAccountBody({
      tool: opts.tool || opts.tool_slug,
      tool_slug: opts.tool_slug || opts.tool,
      title: opts.title,
      body_markdown: opts.body_markdown || opts.content || '',
      content: opts.content || opts.body_markdown || '',
      input_payload: opts.input_payload || opts.metadata || {},
      metadata: Object.assign({ account_file: true }, opts.metadata || {})
    });
    var res = await apiFetch('/api/artifacts', { method: 'POST', body: JSON.stringify(body) });
    var data = await res.json();
    if (!res.ok || data.ok === false) {
      var msg = (data.error && data.error.message) || 'Save failed';
      throw new Error(msg);
    }
    var file = data.data || data;
    var fileId = file.id || file.artifact_id;
    if (fileId) setActiveFileId(fileId);
    global.dispatchEvent(new CustomEvent('opora:file-saved', { detail: { account_id: getId(), file: file } }));
    return file;
  }

  async function getFile(fileId) {
    var res = await apiFetch('/api/artifacts/' + encodeURIComponent(fileId));
    if (!res.ok) throw new Error('File not found');
    var data = await res.json();
    return data.data || data;
  }

  function dashboardFilesUrl() {
    var dash = (global.OporaEdge && global.OporaEdge.DASHBOARD_URL) || 'https://dashboard.oporasupply.com';
    var id = getId();
    return dash + '/?view=workspace&ws=files' + (id ? '&account_id=' + encodeURIComponent(id) : '');
  }

  function getContext() {
    return {
      account_id: getId(),
      active_file_id: getActiveFileId()
    };
  }

  global.OporaAccountFile = {
    init: init,
    getId: getId,
    shortId: shortId,
    getActiveFileId: getActiveFileId,
    setActiveFileId: setActiveFileId,
    accountHeaders: accountHeaders,
    withAccountBody: withAccountBody,
    scopedUrl: scopedUrl,
    listFiles: listFiles,
    saveFile: saveFile,
    getFile: getFile,
    getContext: getContext,
    dashboardFilesUrl: dashboardFilesUrl
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

})(window);
